// src/pages/accounting/VofStatementPage.jsx
// ============================================================================
// Reports > VOF statement (admins only).
//
// The hours Architecture BRIO V.O.F. invoices Studio BRIO Pvt Ltd under the
// intercompany agreement, read from public.vof_hours_statement, which does
// all the classifying (Module A, Module B, SBPL own work, excluded). This page
// picks the period, groups the rows (buildStatement), shows the partner rates
// and exports xlsx and CSV. It never writes a time entry.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Plus, ArrowRightLeft, Undo2, Pencil } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { requireSession } from '../../lib/supabaseAuthHelpers';
import { formatSupabaseError, normalizeId } from '../../hooks/useSupabase';
import {
  BrioFrame,
  BrioTableWrapper,
  BrioTable,
  BrioButton,
  BrioSelect,
  BrioInput,
  BrioInfoBanner,
  BrioEmptyState,
  BrioSectionHeader,
  BrioFullScreenButton,
  useFullScreenPage,
} from '../../components/BrioComponents';
import {
  contractQuarters,
  calendarQuarters,
  calendarMonths,
  todayIso,
  formatDay,
  mapStatementRow,
  buildStatement,
  statementToCsv,
  statementFileName,
  EXCLUDED_BUCKETS,
  sortRows,
  withRateRanges,
} from '../../utils/vofStatement';
import { exportVofStatementXlsx } from '../../utils/vofStatementXlsx';

const PAGE = 1000; // PostgREST row cap per request
const VIEW_COLUMNS = 'entry_id, date, user_id, user_name, is_vof_partner, project_id, project_code, project_name, contracting_entity, task_id, stage, hours, description, notes, classification, reason, rate, currency, amount, override_id, override_reason, override_by_name, override_at';

const fmtH = (n) => (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const fmtMoney = (n, cur) => `${cur === 'EUR' ? '€ ' : cur && cur !== 'mixed' ? `${cur} ` : ''}${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const download = (data, fileName, type) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const num = { textAlign: 'right', whiteSpace: 'nowrap' };
const nowrap = { whiteSpace: 'nowrap' };
// The two entry tables carry many columns; a smaller type keeps them inside
// the page frame. Full screen widens them further.
const entryTable = { fontSize: 12 };
// Space above every section, so each table reads as its own block.
const SECTION_GAP = 'mt-12 mb-3';

// Description with the note under it in grey. A note identical to the
// description is shown once; a note is never dropped.
const DescCell = ({ description, notes }) => {
  const d = (description || '').trim();
  const n = (notes || '').trim();
  return (
    <td style={{ minWidth: 110 }}>
      {d}
      {n && n !== d && (
        <div className="brio-analytics-muted" style={{ fontSize: 11 }}>{n}</div>
      )}
    </td>
  );
};

// Column sorting, the same UI as the app's other tables: click a header to
// sort by it, click again to reverse; the active column shows ↑ or ↓.
const useSort = (key, direction = 'asc') => {
  const [sort, setSort] = useState({ key, direction });
  const toggle = (k) => setSort(prev => ({ key: k, direction: prev.key === k && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const indicator = (k) => (sort.key !== k ? '' : sort.direction === 'asc' ? ' ↑' : ' ↓');
  return { sort, toggle, indicator };
};

const SortTh = ({ sorter, k, style, children }) => (
  <th
    style={{ cursor: 'pointer', userSelect: 'none', ...style }}
    onClick={() => sorter.toggle(k)}
    aria-sort={sorter.sort.key === k ? (sorter.sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
  >
    {children}{sorter.indicator(k)}
  </th>
);

const MOVE_REASONS_KEY = 'vof_move_reasons';
const ADD_REASON = '__add_reason__';

// Unique reasons, case-insensitively, trimmed, in alphabetical order.
const mergeReasons = (...lists) => {
  const seen = new Map();
  lists.flat().forEach(r => {
    const t = (r || '').trim();
    if (t && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
  });
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

const PARTNER_ACC = {
  partner: p => p.userName,
  aH: p => p.moduleA.hours, aAmt: p => p.moduleA.amount,
  bH: p => p.moduleB.hours, bAmt: p => p.moduleB.amount,
  tH: p => p.total.hours, tAmt: p => p.total.amount,
};
const EXCLUDED_ACC = {
  partner: p => p.userName,
  ...Object.fromEntries(EXCLUDED_BUCKETS.map(b => [b, p => p.excluded[b] || 0])),
};
const PROJECT_ACC = { partner: p => p.userName, project: p => p.projectCode, hours: p => p.hours, amount: p => p.amount };
const ENTRY_ACC = {
  date: r => r.date, partner: r => r.userName, module: r => r.classification, reason: r => r.bucket,
  project: r => r.projectCode, stage: r => r.stage, hours: r => r.hours, amount: r => r.amount,
  description: r => [r.description, r.notes].filter(Boolean).join(' '),
};
const RATE_ACC = { partner: r => r.partnerName, rate: r => r.rate, from: r => r.validFrom, to: r => r.validTo || '9999-12-31' };

export default function VofStatementPage({ currentUser }) {
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(false);
  // Reports already wraps every tab in a BrioFrame. A second one here doubled
  // the side padding and squeezed the tables, so the page brings its own frame
  // only in full screen, where the Reports frame is no longer around it.
  const Shell = isFullScreen ? BrioFrame : 'div';
  const today = todayIso();
  const options = useMemo(() => ({
    contract: contractQuarters(today),
    calendar: calendarQuarters(today),
    month: calendarMonths(today),
  }), [today]);

  const [mode, setMode] = useState('contract');
  // Opens on the latest contract quarter that has ended: the one due for invoicing.
  const [pick, setPick] = useState(() => (options.contract.find(o => o.to < today) || options.contract[0])?.id || '');
  const [custom, setCustom] = useState({ from: options.contract[0]?.from || today, to: today });

  const period = useMemo(() => {
    if (mode === 'custom') return { from: custom.from, to: custom.to };
    const list = options[mode] || [];
    const hit = list.find(o => o.id === pick) || list[0];
    return hit ? { from: hit.from, to: hit.to } : { from: today, to: today };
  }, [mode, pick, custom, options, today]);

  const changeMode = (m) => {
    setMode(m);
    if (m !== 'custom') setPick((options[m] || [])[0]?.id || '');
  };

  // ── Statement rows ────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const periodKey = `${period.from}|${period.to}`;
  const periodValid = !!period.from && !!period.to && period.from <= period.to;

  const load = useCallback(async () => {
    if (!periodValid) return;
    const req = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const all = [];
      for (let offset = 0; ; offset += PAGE) {
        const { data, error: err } = await supabase
          .from('vof_hours_statement')
          .select(VIEW_COLUMNS)
          .eq('is_vof_partner', true)
          .gte('date', period.from)
          .lte('date', period.to)
          .order('date', { ascending: true })
          .order('entry_id', { ascending: true })
          .range(offset, offset + PAGE - 1);
        if (err) throw err;
        all.push(...(data || []));
        if (!data || data.length < PAGE) break;
      }
      // A slower response for an earlier period must not overwrite this one.
      if (req !== requestRef.current) return;
      setRows(all.map(mapStatementRow));
      setLoadedFor(`${period.from}|${period.to}`);
    } catch (e) {
      if (req !== requestRef.current) return;
      setError(formatSupabaseError(e, { action: 'load', entity: 'VOF statement', role: currentUser?.role }));
      setRows([]);
      setLoadedFor(null);
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [period.from, period.to, periodValid, currentUser?.role]);

  useEffect(() => { load(); }, [load]);

  // Rows are only read back for the period they were fetched for.
  const statement = useMemo(
    () => buildStatement(loadedFor === periodKey ? rows : []),
    [rows, loadedFor, periodKey],
  );
  const cur = statement.currency;

  const partnerSort = useSort('partner');
  const excludedSumSort = useSort('partner');
  const projectSort = useSort('partner');
  const detailSort = useSort('date');
  const excludedSort = useSort('date');
  const rateSort = useSort('partner');
  const sorted = (rows, acc, sorter) => sortRows(rows, acc, sorter.sort.key, sorter.sort.direction);

  // ── Partner rates ─────────────────────────────────────────────────────────
  const [partners, setPartners] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateForm, setRateForm] = useState(null);
  const [rateError, setRateError] = useState('');

  const loadRates = useCallback(async () => {
    const [{ data: us, error: e1 }, { data: rs, error: e2 }] = await Promise.all([
      supabase.from('users').select('id, name').eq('is_vof_partner', true).is('deleted_at', null).order('name'),
      supabase.from('vof_partner_rates').select('id, user_id, hourly_rate, currency, valid_from').order('valid_from', { ascending: false }),
    ]);
    if (e1 || e2) {
      setRateError(formatSupabaseError(e1 || e2, { action: 'load', entity: 'partner rates', role: currentUser?.role }));
      return;
    }
    setPartners((us || []).map(u => ({ id: normalizeId(u.id), name: u.name })));
    setRates((rs || []).map(r => ({
      id: normalizeId(r.id), userId: normalizeId(r.user_id), rate: Number(r.hourly_rate), currency: r.currency, validFrom: r.valid_from,
    })));
  }, [currentUser?.role]);

  useEffect(() => { loadRates(); }, [loadRates]);

  const partnerName = (id) => partners.find(p => String(p.id) === String(id))?.name || `User ${id}`;

  // Editing an existing rate in place. Ranges are derived (withRateRanges), so
  // moving one rate's start also moves the end of that partner's rate before it.
  const [rateEdit, setRateEdit] = useState(null);

  const saveRateEdit = async () => {
    setRateError('');
    const rate = Number(rateEdit.rate);
    if (!(rate >= 0) || rateEdit.rate === '') return setRateError('Enter a rate.');
    if (!/^[A-Za-z]{3}$/.test(rateEdit.currency || '')) return setRateError('Currency must be a three letter code, for example EUR.');
    if (!rateEdit.validFrom) return setRateError('Enter the date the rate applies from.');
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('vof_partner_rates')
        .update({ hourly_rate: rate, currency: rateEdit.currency.toUpperCase(), valid_from: rateEdit.validFrom })
        .eq('id', rateEdit.id)
        .select('id');
      if (err) {
        return setRateError(err.code === '23505'
          ? 'This partner already has a rate starting on that date.'
          : formatSupabaseError(err, { action: 'save', entity: 'partner rate', role: currentUser?.role }));
      }
      // An UPDATE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) return setRateError('Not saved: you may not edit partner rates.');
      setRateEdit(null);
      await loadRates();
      await load();
    } catch (e) {
      setRateError(e.message);
    }
    return undefined;
  };

  const saveRate = async () => {
    setRateError('');
    const rate = Number(rateForm.rate);
    if (!rateForm.userId) return setRateError('Choose a partner.');
    if (!(rate >= 0) || rateForm.rate === '') return setRateError('Enter a rate.');
    if (!rateForm.validFrom) return setRateError('Enter the date the rate applies from.');
    try {
      await requireSession();
      const { error: err } = await supabase.from('vof_partner_rates').insert({
        user_id: normalizeId(rateForm.userId),
        hourly_rate: rate,
        currency: (rateForm.currency || 'EUR').toUpperCase(),
        valid_from: rateForm.validFrom,
      });
      if (err) return setRateError(formatSupabaseError(err, { action: 'save', entity: 'partner rate', role: currentUser?.role }));
      setRateForm(null);
      await loadRates();
      await load();
    } catch (e) {
      setRateError(e.message);
    }
  };

  // ── Move to SBPL ──────────────────────────────────────────────────────────
  // An admin moves Module A or B entries to SBPL with a reason. The override
  // lives in vof_entry_overrides; the time entry is never touched, and the
  // view ignores an override on anything that is not Module A or B.
  const [selected, setSelected] = useState(() => new Set());
  const [moveReason, setMoveReason] = useState('');
  const [pastReasons, setPastReasons] = useState([]);
  const [moveError, setMoveError] = useState('');
  const [moving, setMoving] = useState(false);

  // Selection belongs to the period it was made in.
  useEffect(() => { setSelected(new Set()); setMoveError(''); }, [periodKey]);

  // The reason dropdown offers the saved list (app_settings 'vof_move_reasons',
  // added to with "Add reason…") plus every reason already used on a move.
  const [savedReasons, setSavedReasons] = useState([]);
  const [addingReason, setAddingReason] = useState(false);
  const [newReason, setNewReason] = useState('');

  const loadReasons = useCallback(async () => {
    const [used, saved] = await Promise.all([
      supabase.from('vof_entry_overrides').select('reason, created_at').order('created_at', { ascending: false }).limit(500),
      supabase.from('app_settings').select('value').eq('key', MOVE_REASONS_KEY).limit(1),
    ]);
    const savedList = Array.isArray(saved.data?.[0]?.value) ? saved.data[0].value.filter(r => typeof r === 'string') : [];
    const usedList = used.error ? [] : (used.data || []).map(r => r.reason);
    setSavedReasons(savedList);
    setPastReasons(mergeReasons(savedList, usedList));
    // Autofill: start from the reason used last, unless one is already chosen.
    const lastUsed = (usedList.find(r => (r || '').trim()) || '').trim();
    setMoveReason(prev => prev || lastUsed || mergeReasons(savedList, [])[0] || '');
  }, []);

  const saveNewReason = async () => {
    const reason = newReason.trim();
    if (!reason) return setMoveError('Type the new reason first.');
    const existing = pastReasons.find(r => r.toLowerCase() === reason.toLowerCase());
    if (existing) {
      setMoveReason(existing);
      setAddingReason(false);
      setNewReason('');
      return undefined;
    }
    try {
      await requireSession();
      const next = mergeReasons([...savedReasons, reason], []);
      const { error: err } = await supabase.from('app_settings').upsert({ key: MOVE_REASONS_KEY, value: next });
      if (err) return setMoveError(formatSupabaseError(err, { action: 'save', entity: 'reason', role: currentUser?.role }));
      setSavedReasons(next);
      setPastReasons(prev => mergeReasons(next, prev));
      setMoveReason(reason);
      setAddingReason(false);
      setNewReason('');
      setMoveError('');
    } catch (e) {
      setMoveError(e.message);
    }
    return undefined;
  };

  useEffect(() => { loadReasons(); }, [loadReasons]);

  const detailIds = useMemo(() => statement.detail.map(r => r.entryId), [statement.detail]);
  const selectedHours = useMemo(
    () => statement.detail.filter(r => selected.has(r.entryId)).reduce((n, r) => n + r.hours, 0),
    [statement.detail, selected],
  );
  const allSelected = detailIds.length > 0 && detailIds.every(id => selected.has(id));
  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(detailIds));

  const moveToSbpl = async () => {
    setMoveError('');
    const reason = moveReason.trim();
    const ids = detailIds.filter(id => selected.has(id));
    if (ids.length === 0) return setMoveError('Tick the entries to move first.');
    if (!reason) return setMoveError('Enter a reason. It is the only record of why these hours are not invoiced.');
    setMoving(true);
    try {
      await requireSession();
      const { error: err } = await supabase
        .from('vof_entry_overrides')
        .insert(ids.map(id => ({ entry_id: id, reason })));
      if (err) {
        setMoveError(formatSupabaseError(err, { action: 'move', entity: 'entries to SBPL', role: currentUser?.role }));
        return;
      }
      setSelected(new Set());
      await Promise.all([load(), loadReasons()]);
    } catch (e) {
      setMoveError(e.message);
    } finally {
      setMoving(false);
    }
  };

  const undoMove = async (row) => {
    setMoveError('');
    try {
      await requireSession();
      const { data, error: err } = await supabase
        .from('vof_entry_overrides')
        .update({ revoked_at: new Date().toISOString(), revoked_by: normalizeId(currentUser?.id) })
        .eq('id', row.overrideId)
        .is('revoked_at', null)
        .select('id');
      if (err) return setMoveError(formatSupabaseError(err, { action: 'undo', entity: 'move to SBPL', role: currentUser?.role }));
      // An UPDATE that RLS filters out matches zero rows instead of raising.
      if (!data || data.length === 0) {
        setMoveError('Not undone: this move may already have been undone elsewhere. The statement has been reloaded.');
      }
      await load();
    } catch (e) {
      setMoveError(e.message);
    }
  };

  // ── Exports ───────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const exportCsv = () => {
    // Byte order mark so Excel reads the CSV as UTF 8.
    download(`﻿${statementToCsv(statement, period)}`, statementFileName(period, 'csv'), 'text/csv;charset=utf-8');
  };
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { buffer, fileName } = await exportVofStatementXlsx(statement, period);
      download(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
    } finally {
      setExporting(false);
    }
  };

  const ready = loadedFor === periodKey && !loading;
  const empty = ready && statement.partners.length === 0;

  return (
    <div className={rootClassName} style={{ background: 'var(--brio-white, #fff)', ...rootStyle }}>
    <Shell>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
      </div>
      <div style={{ margin: '16px 0 4px', fontSize: 13 }} className="brio-analytics-muted">
        Partner hours Architecture BRIO V.O.F. invoices Studio BRIO Pvt Ltd. Module A: SBPL projects worked from outside India.
        Module B: Atlas development (BRA, Atlas). Days in a travel log entry for India, BRG and other BRA work, and ABVOF projects are left out.
      </div>

      {/* Period picker */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '16px 0' }}>
        <BrioSelect value={mode} onChange={e => changeMode(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="contract">Contract quarter</option>
          <option value="calendar">Calendar quarter</option>
          <option value="month">Month</option>
          <option value="custom">Custom range</option>
        </BrioSelect>
        {mode === 'custom' ? (
          <>
            <BrioInput type="date" value={custom.from} onChange={e => setCustom({ ...custom, from: e.target.value })} style={{ maxWidth: 170 }} />
            <span className="brio-analytics-muted">to</span>
            <BrioInput type="date" value={custom.to} min={custom.from} onChange={e => setCustom({ ...custom, to: e.target.value })} style={{ maxWidth: 170 }} />
          </>
        ) : (
          <BrioSelect value={pick} onChange={e => setPick(e.target.value)} style={{ maxWidth: 300 }}>
            {(options[mode] || []).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </BrioSelect>
        )}
        <div style={{ flex: 1 }} />
        <BrioButton variant="secondary" onClick={exportCsv} disabled={!ready || empty}>
          <Download size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />CSV
        </BrioButton>
        <BrioButton onClick={exportXlsx} disabled={!ready || empty || exporting}>
          <Download size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />{exporting ? 'Exporting…' : 'Excel'}
        </BrioButton>
      </div>

      {!periodValid && <BrioInfoBanner>The end date is before the start date.</BrioInfoBanner>}
      {error && <BrioInfoBanner>{error}</BrioInfoBanner>}
      {ready && statement.missingRate > 0 && (
        <BrioInfoBanner>
          {statement.missingRate} billable {statement.missingRate === 1 ? 'entry has' : 'entries have'} no rate for its date and {statement.missingRate === 1 ? 'is' : 'are'} counted at 0. Add a rate below that starts on or before the period.
        </BrioInfoBanner>
      )}

      {!ready ? (
        <BrioEmptyState>Loading statement…</BrioEmptyState>
      ) : empty ? (
        <BrioEmptyState>No partner hours between {formatDay(period.from)} and {formatDay(period.to)}.</BrioEmptyState>
      ) : (
        <>
          {/* Summary */}
          <BrioSectionHeader className={SECTION_GAP} title={`Summary, ${formatDay(period.from)} to ${formatDay(period.to)}`} />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  <SortTh sorter={partnerSort} k="partner">Partner</SortTh>
                  <SortTh sorter={partnerSort} k="aH" style={num}>Module A h</SortTh>
                  <SortTh sorter={partnerSort} k="aAmt" style={num}>Module A</SortTh>
                  <SortTh sorter={partnerSort} k="bH" style={num}>Module B h</SortTh>
                  <SortTh sorter={partnerSort} k="bAmt" style={num}>Module B</SortTh>
                  <SortTh sorter={partnerSort} k="tH" style={num}>Total h</SortTh>
                  <SortTh sorter={partnerSort} k="tAmt" style={num}>Total</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted(statement.partners, PARTNER_ACC, partnerSort).map(p => (
                  <tr key={p.userId}>
                    <td style={{ textTransform: 'capitalize' }}>{p.userName}</td>
                    <td style={num}>{fmtH(p.moduleA.hours)}</td>
                    <td style={num}>{fmtMoney(p.moduleA.amount, cur)}</td>
                    <td style={num}>{fmtH(p.moduleB.hours)}</td>
                    <td style={num}>{fmtMoney(p.moduleB.amount, cur)}</td>
                    <td style={num}>{fmtH(p.total.hours)}</td>
                    <td style={num}>{fmtMoney(p.total.amount, cur)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 600 }}>
                  <td>Total</td>
                  <td style={num}>{fmtH(statement.totals.moduleAHours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.moduleAAmount, cur)}</td>
                  <td style={num}>{fmtH(statement.totals.moduleBHours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.moduleBAmount, cur)}</td>
                  <td style={num}>{fmtH(statement.totals.hours)}</td>
                  <td style={num}>{fmtMoney(statement.totals.amount, cur)}</td>
                </tr>
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Excluded per partner */}
          <BrioSectionHeader className={SECTION_GAP} title="Excluded hours per partner" />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  <SortTh sorter={excludedSumSort} k="partner">Partner</SortTh>
                  {EXCLUDED_BUCKETS.map(b => <SortTh key={b} sorter={excludedSumSort} k={b} style={{ textAlign: 'right', verticalAlign: 'bottom' }}>{b}</SortTh>)}
                </tr>
              </thead>
              <tbody>
                {sorted(statement.partners, EXCLUDED_ACC, excludedSumSort).map(p => (
                  <tr key={p.userId}>
                    <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{p.userName}</td>
                    {EXCLUDED_BUCKETS.map(b => <td key={b} style={num}>{fmtH(p.excluded[b])}</td>)}
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Module A per project */}
          <BrioSectionHeader className={SECTION_GAP} title="Module A per project" />
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  <SortTh sorter={projectSort} k="partner">Partner</SortTh>
                  <SortTh sorter={projectSort} k="project">Project</SortTh>
                  <SortTh sorter={projectSort} k="hours" style={num}>Hours</SortTh>
                  <SortTh sorter={projectSort} k="amount" style={num}>Amount</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted(statement.projectsA, PROJECT_ACC, projectSort).map(p => (
                  <tr key={`${p.userId}|${p.projectId}`}>
                    <td style={{ textTransform: 'capitalize' }}>{p.userName}</td>
                    <td><strong>{p.projectCode}</strong> {p.projectName}</td>
                    <td style={num}>{fmtH(p.hours)}</td>
                    <td style={num}>{fmtMoney(p.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Detail */}
          <BrioSectionHeader className={SECTION_GAP} title={`Detail, Module A and Module B (${statement.detail.length} entries)`} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 8px' }}>
            <span className="brio-analytics-muted" style={{ fontSize: 12 }}>
              {selected.size > 0 ? `${selected.size} selected, ${fmtH(selectedHours)} h` : 'Tick entries SBPL bills itself to move them out of the invoice.'}
            </span>
            <div style={{ flex: 1 }} />
            {addingReason ? (
              <>
                <BrioInput
                  autoFocus
                  placeholder="New reason"
                  aria-label="New reason"
                  value={newReason}
                  onChange={e => setNewReason(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveNewReason();
                    if (e.key === 'Escape') { setAddingReason(false); setNewReason(''); }
                  }}
                  style={{ minWidth: 200, maxWidth: 280 }}
                />
                <BrioButton variant="secondary" onClick={saveNewReason}>Add</BrioButton>
                <BrioButton variant="secondary" onClick={() => { setAddingReason(false); setNewReason(''); }}>Cancel</BrioButton>
              </>
            ) : (
              <BrioSelect
                aria-label="Reason for moving to SBPL"
                value={moveReason}
                onChange={e => {
                  if (e.target.value === ADD_REASON) { setAddingReason(true); setNewReason(''); }
                  else setMoveReason(e.target.value);
                }}
                style={{ minWidth: 220, maxWidth: 320 }}
              >
                {!moveReason && <option value="">Choose a reason…</option>}
                {pastReasons.map(r => <option key={r} value={r}>{r}</option>)}
                <option value={ADD_REASON}>+ Add reason…</option>
              </BrioSelect>
            )}
            <BrioButton onClick={moveToSbpl} disabled={selected.size === 0 || moving}>
              <ArrowRightLeft size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />{moving ? 'Moving…' : 'Move to SBPL'}
            </BrioButton>
          </div>
          {moveError && <BrioInfoBanner>{moveError}</BrioInfoBanner>}
          <BrioTableWrapper maxHeight={520}>
            <BrioTable stickyHeader style={entryTable}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input type="checkbox" aria-label="Select all entries" checked={allSelected} onChange={toggleAll} />
                  </th>
                  <SortTh sorter={detailSort} k="date">Date</SortTh>
                  <SortTh sorter={detailSort} k="partner">Partner</SortTh>
                  <SortTh sorter={detailSort} k="module">Module</SortTh>
                  <SortTh sorter={detailSort} k="project">Project</SortTh>
                  <SortTh sorter={detailSort} k="stage">Stage</SortTh>
                  <SortTh sorter={detailSort} k="hours" style={num}>Hours</SortTh>
                  <SortTh sorter={detailSort} k="amount" style={num}>Amount</SortTh>
                  <SortTh sorter={detailSort} k="description">Description and notes</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted(statement.detail, ENTRY_ACC, detailSort).map(r => (
                  <tr key={r.entryId}>
                    <td>
                      <input type="checkbox" aria-label={`Select entry ${r.entryId}`} checked={selected.has(r.entryId)} onChange={() => toggleRow(r.entryId)} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(r.date)}</td>
                    <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{r.userName}</td>
                    <td style={nowrap}>{r.classification}</td>
                    <td style={nowrap}>{r.projectCode}</td>
                    <td style={nowrap}>{r.stage}</td>
                    <td style={num}>{fmtH(r.hours)}</td>
                    <td style={num}>{r.amount == null ? 'no rate' : fmtMoney(r.amount, r.currency)}</td>
                    <DescCell description={r.description} notes={r.notes} />
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>

          {/* Excluded rows */}
          <BrioSectionHeader className={SECTION_GAP} title={`Excluded partner entries (${statement.excludedRows.length})`} />
          <BrioTableWrapper maxHeight={520}>
            <BrioTable stickyHeader style={entryTable}>
              <thead>
                <tr>
                  <SortTh sorter={excludedSort} k="date">Date</SortTh>
                  <SortTh sorter={excludedSort} k="partner">Partner</SortTh>
                  <SortTh sorter={excludedSort} k="reason">Reason</SortTh>
                  <SortTh sorter={excludedSort} k="project">Project</SortTh>
                  <SortTh sorter={excludedSort} k="stage">Stage</SortTh>
                  <SortTh sorter={excludedSort} k="hours" style={num}>Hours</SortTh>
                  <SortTh sorter={excludedSort} k="description">Description and notes</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted(statement.excludedRows, ENTRY_ACC, excludedSort).map(r => (
                  <tr key={r.entryId}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(r.date)}</td>
                    <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{r.userName}</td>
                    <td style={{ minWidth: 120 }}>
                      {/* Undo sits beside the reason, so it never depends on a last column fitting. */}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {r.bucket}
                        {r.overrideId != null && (
                          <button
                            type="button"
                            onClick={() => undoMove(r)}
                            title="Undo: put this entry back on the invoice"
                            aria-label="Undo move to SBPL"
                            style={{ background: 'none', border: 0, cursor: 'pointer', padding: 2, color: 'var(--brio-gold, #9F902A)', lineHeight: 0 }}
                          >
                            <Undo2 size={14} />
                          </button>
                        )}
                      </span>
                      {r.overrideId != null && (
                        <div className="brio-analytics-muted" style={{ fontSize: 11 }}>
                          {r.overrideReason}{r.overrideByName && <>, by <span style={{ textTransform: 'capitalize' }}>{r.overrideByName}</span></>}{r.overrideAt ? `, ${formatDay(String(r.overrideAt).slice(0, 10))}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={nowrap}>{r.projectCode}</td>
                    <td style={nowrap}>{r.stage}</td>
                    <td style={num}>{fmtH(r.hours)}</td>
                    <DescCell description={r.description} notes={r.notes} />
                  </tr>
                ))}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>
        </>
      )}

      {/* Partner rates */}
      <BrioSectionHeader
        className={SECTION_GAP}
        title="Partner rates"
        right={!rateForm && (
          <BrioButton variant="secondary" onClick={() => { setRateError(''); setRateForm({ userId: '', rate: '', currency: 'EUR', validFrom: period.from }); }}>
            <Plus size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />New rate
          </BrioButton>
        )}
      />
      <div className="brio-analytics-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Each entry takes the rate whose range covers its date. A rate runs until the day before the partner's next rate starts. Editing a rate changes every statement in its range; to change the rate from a date onwards, add a new rate instead.
      </div>
      {rateForm && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <BrioSelect value={rateForm.userId} onChange={e => setRateForm({ ...rateForm, userId: e.target.value })} style={{ maxWidth: 200 }}>
            <option value="">Partner…</option>
            {partners.map(p => <option key={p.id} value={String(p.id)} style={{ textTransform: 'capitalize' }}>{p.name}</option>)}
          </BrioSelect>
          <BrioInput type="number" min="0" step="0.01" placeholder="Rate per hour" value={rateForm.rate} onChange={e => setRateForm({ ...rateForm, rate: e.target.value })} style={{ maxWidth: 140 }} />
          <BrioInput maxLength={3} value={rateForm.currency} onChange={e => setRateForm({ ...rateForm, currency: e.target.value.toUpperCase() })} style={{ maxWidth: 70 }} />
          <span className="brio-analytics-muted">from</span>
          <BrioInput type="date" value={rateForm.validFrom} onChange={e => setRateForm({ ...rateForm, validFrom: e.target.value })} style={{ maxWidth: 170 }} />
          <BrioButton onClick={saveRate}>Save</BrioButton>
          <BrioButton variant="secondary" onClick={() => setRateForm(null)}>Cancel</BrioButton>
        </div>
      )}
      {rateError && <BrioInfoBanner>{rateError}</BrioInfoBanner>}
      <BrioTableWrapper>
        <BrioTable>
          <thead>
            <tr>
              <SortTh sorter={rateSort} k="partner">Partner</SortTh>
              <SortTh sorter={rateSort} k="rate" style={num}>Rate per hour</SortTh>
              <SortTh sorter={rateSort} k="from">From</SortTh>
              <SortTh sorter={rateSort} k="to">To</SortTh>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted(withRateRanges(rates).map(r => ({ ...r, partnerName: partnerName(r.userId) })), RATE_ACC, rateSort).map(r => (
              rateEdit && rateEdit.id === r.id ? (
                <tr key={r.id}>
                  <td style={{ textTransform: 'capitalize' }}>{r.partnerName}</td>
                  <td style={num}>
                    <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
                      <BrioInput
                        type="number" min="0" step="0.01" aria-label="Rate per hour"
                        value={rateEdit.rate}
                        onChange={e => setRateEdit({ ...rateEdit, rate: e.target.value })}
                        style={{ width: 100 }}
                      />
                      <BrioInput
                        maxLength={3} aria-label="Currency"
                        value={rateEdit.currency}
                        onChange={e => setRateEdit({ ...rateEdit, currency: e.target.value.toUpperCase() })}
                        style={{ width: 60 }}
                      />
                    </span>
                  </td>
                  <td>
                    <BrioInput
                      type="date" aria-label="From"
                      value={rateEdit.validFrom}
                      onChange={e => setRateEdit({ ...rateEdit, validFrom: e.target.value })}
                      style={{ width: 150 }}
                    />
                  </td>
                  <td className="brio-analytics-muted" style={{ fontSize: 12 }}>follows the next rate</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <BrioButton onClick={saveRateEdit}>Save</BrioButton>{' '}
                    <BrioButton variant="secondary" onClick={() => { setRateEdit(null); setRateError(''); }}>Cancel</BrioButton>
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td style={{ textTransform: 'capitalize' }}>{r.partnerName}</td>
                  <td style={num}>{fmtMoney(r.rate, r.currency)}</td>
                  <td style={nowrap}>{formatDay(r.validFrom)}</td>
                  <td style={nowrap}>{r.validTo ? formatDay(r.validTo) : <span className="brio-analytics-muted">onwards</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <BrioButton
                      variant="secondary"
                      onClick={() => { setRateError(''); setRateEdit({ id: r.id, rate: String(r.rate), currency: r.currency || 'EUR', validFrom: r.validFrom }); }}
                      title="Edit this rate"
                      aria-label={`Edit rate from ${formatDay(r.validFrom)}`}
                    >
                      <Pencil size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Edit
                    </BrioButton>
                  </td>
                </tr>
              )
            ))}
            {rates.length === 0 && (
              <tr><td colSpan={5} className="brio-analytics-muted">No rates yet.</td></tr>
            )}
          </tbody>
        </BrioTable>
      </BrioTableWrapper>
    </Shell>
    </div>
  );
}
